import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight, Mail } from "lucide-react";
import shared from "@/seo/shared";
import { Logo } from "./Logo";
import { COMPANY, NAV } from "./data";
import { EASE, scrollToId } from "./scroll";

const MobileMenu = ({ open, go }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-ink px-6 pb-32 pt-28 lg:hidden"
        data-lenis-prevent
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.6, ease: EASE }}
        data-testid="mobile-menu"
      >
        <ul className="flex flex-col gap-1">
          {NAV.map((n, i) => (
            <motion.li key={n.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.5, ease: EASE }}>
              <a href={`/#${n.id}`} onClick={go(n.id)} data-testid={`mobile-nav-link-${n.id}`}
                className="flex items-baseline justify-between border-b border-white/10 py-4 font-display text-4xl font-bold uppercase text-white">
                {n.label}<span className="text-sm text-terra">0{i + 1}</span>
              </a>
            </motion.li>
          ))}
        </ul>
        <ul className="mt-6 grid grid-cols-2 gap-2" aria-label="Leistungen">
          {shared.services.map((s) => (
            <li key={s.slug}><Link to={`/${s.slug}`} className="block rounded-full border border-white/15 px-3 py-2 text-xs font-semibold text-slate-100" data-testid={`mobile-service-${s.sid}`}>{s.name}</Link></li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3">
          <a href={COMPANY.phoneHref} className="btn-ghost justify-center" data-testid="mobile-menu-phone"><Phone size={18} /> {COMPANY.phoneDisplay}</a>
          <a href={`mailto:${COMPANY.email}`} className="btn-ghost justify-center" data-testid="mobile-menu-email"><Mail size={18} /> E-Mail schreiben</a>
          <a href="#kontakt" onClick={go("kontakt")} className="btn-primary justify-center" data-testid="mobile-menu-cta">Kostenloses Angebot <ArrowUpRight size={18} /></a>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const go = (id) => (e) => {
    setOpen(false);
    if (!document.getElementById(id)) return;
    e.preventDefault();
    setTimeout(() => scrollToId(id), open ? 350 : 0);
  };

  const solid = scrolled || open;
  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,padding] duration-500 ${solid ? "border-white/10 bg-ink/85 py-3 backdrop-blur-xl" : "border-transparent bg-transparent py-5"}`}
        data-testid="site-header"
      >
        <nav className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12" aria-label="Hauptnavigation">
          <a href="/" onClick={go("start")} className="group" aria-label="Christian Bedachungen – zur Startseite" data-testid="nav-logo"><Logo /></a>
          <ul className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <li key={n.id} className={n.id === "leistungen" ? "group/menu relative" : ""}>
                <a href={`/#${n.id}`} onClick={go(n.id)} className="nav-link" data-testid={`nav-link-${n.id}`}>{n.label}</a>
                {n.id === "leistungen" && (
                  <div className="invisible absolute left-1/2 top-full w-[21rem] -translate-x-1/2 pt-5 opacity-0 transition-[opacity,visibility] duration-300 group-focus-within/menu:visible group-focus-within/menu:opacity-100 group-hover/menu:visible group-hover/menu:opacity-100" data-testid="nav-services-dropdown">
                    <ul className="border border-white/10 bg-ink2/95 p-2 shadow-2xl backdrop-blur-xl">
                      {shared.services.map((s) => (
                        <li key={s.slug}>
                          <Link to={`/${s.slug}`} className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-terra-deep hover:text-white" data-testid={`nav-service-${s.sid}`}>{s.name}<ArrowUpRight size={14} /></Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <a href={COMPANY.phoneHref} className="hidden items-center gap-2 text-sm font-semibold text-white transition-colors hover:text-terra xl:flex" data-testid="nav-phone-link">
              <Phone size={16} className="text-terra" /> {COMPANY.phoneDisplay}
            </a>
            <a href={`mailto:${COMPANY.email}`} aria-label="E-Mail schreiben" title="E-Mail schreiben" className="hidden h-11 w-11 place-items-center rounded-full border border-white/20 text-white transition-colors hover:border-terra hover:text-terra lg:grid" data-testid="nav-email-link">
              <Mail size={17} />
            </a>
            <a href="#kontakt" onClick={go("kontakt")} className="btn-primary hidden !px-6 !py-3 sm:inline-flex" data-testid="nav-cta-button">
              Kostenloses Angebot <ArrowUpRight size={16} />
            </a>
            <a href={COMPANY.phoneHref} aria-label={`Jetzt anrufen: ${COMPANY.phoneDisplay}`} className="grid h-11 w-11 place-items-center rounded-full bg-terra-deep text-white lg:hidden" data-testid="nav-mobile-phone">
              <Phone size={19} />
            </a>
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? "Menü schließen" : "Menü öffnen"}
              className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white lg:hidden" data-testid="nav-mobile-menu-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </motion.header>
      <MobileMenu open={open} go={go} />
    </>
  );
};
